import { FC, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LinkButton } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { LocalPronunciationClip } from 'src/apps/Monetisation/PronunciationUpsell/useLocalPronunciationClip';
import {
  getModalDismissMethod,
  PronunciationUpsellSource,
  PronunciationUpsellTrigger,
  usePronunciationUpsell,
} from 'src/apps/Monetisation/PronunciationUpsell/usePronunciationUpsell';
import * as S from './styles';

const msgs = defineMessages({
  banner: {
    id: 'pronunciation_upsell_daily_limit_banner',
    defaultMessage: "You've used up your free pronunciation lesson for today",
  },
  title: {
    id: 'pronunciation_upsell_daily_limit_title',
    defaultMessage: 'Keep improving how you sound.',
  },
  description: {
    id: 'pronunciation_upsell_daily_limit_description',
    defaultMessage: 'Upgrade to PRO. Access unlimited pronunciation practice with locals.',
  },
  planPeriod: {
    id: 'new_plans_card_12_months',
    defaultMessage: '12 months',
  },
  socialProof: {
    id: 'pronunciation_upsell_social_proof',
    defaultMessage: 'Trusted by <strong>370,000+ PRO learners</strong>',
  },
  clipCaption: {
    id: 'pronunciation_upsell_clip_caption',
    defaultMessage: '“{word}”',
  },
  cta: {
    id: 'pronunciation_upsell_cta',
    defaultMessage: 'Improve your pronunciation',
  },
  skip: {
    id: 'pronunciation_upsell_skip',
    defaultMessage: 'Stay on free',
  },
});

const AVATARS = [
  { letter: 'M', color: 'secondary20' },
  { letter: 'A', color: 'warning20' },
  { letter: 'L', color: 'tertiary30' },
] as const;

export type PronunciationLimitUpsellProps = {
  trigger: PronunciationUpsellTrigger;
  onContinue: () => void;
  source?: PronunciationUpsellSource;
  /** Replaces the daily-lesson banner where the limit is counted differently. */
  banner?: string;
  /** Overrides the Learn with Locals clip, primarily for previews and tests. */
  clip?: LocalPronunciationClip;
};

/** Floats over the pronunciation screen the learner is on once their daily lesson is spent. */
export const PronunciationLimitUpsell: FC<PronunciationLimitUpsellProps> = ({
  trigger,
  onContinue,
  source = 'pronunciation_no_tokens',
  banner,
  clip: clipOverride,
}) => {
  const intl = useIntl();
  const { clip, formattedYearlyPrice, handleSubscribeClick, handleDismiss } =
    usePronunciationUpsell({ source, onContinue, clipOverride, trigger });

  return (
    <S.Modal
      isOpen
      hideCloseButton
      handleClose={event => handleDismiss(getModalDismissMethod(event))}
      $hasVideo={!!clip}
    >
      <S.Layout data-testid="pronunciation-upsell" $hasVideo={!!clip}>
        {clip && (
          <S.Stage>
            <S.Clip src={clip.src} playOnKeyboardShortcut={false} autoPlay />
            <S.Phrase dir="auto">
              {intl.formatMessage(msgs.clipCaption, { word: clip.phrase })}
            </S.Phrase>
          </S.Stage>
        )}
        <S.Details>
          <S.Banner data-testid="pronunciation-upsell-banner">
            <S.BannerIcon name="padlock" aria-hidden />
            {banner ?? intl.formatMessage(msgs.banner)}
          </S.Banner>
          <S.Header>
            <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
            <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
          </S.Header>
          <S.PlanRow>
            <span>{intl.formatMessage(msgs.planPeriod)}</span>
            {formattedYearlyPrice && (
              <span data-testid="pronunciation-upsell-price">{formattedYearlyPrice}</span>
            )}
          </S.PlanRow>
          <S.SocialProof>
            <S.Avatars aria-hidden>
              {AVATARS.map(({ letter, color }) => (
                <S.Avatar key={letter} $color={color}>
                  {letter}
                </S.Avatar>
              ))}
            </S.Avatars>
            <span>
              {intl.formatMessage(msgs.socialProof, {
                strong: (...chunks: ReactNode[]) => <strong key="strong">{chunks}</strong>,
              })}
            </span>
          </S.SocialProof>
          <S.Actions>
            <LinkButton
              buttonType="primary"
              buttonSize="large"
              wide
              href={{ pathname: ROUTES.payment.plans, query: { source } }}
              onClick={handleSubscribeClick}
            >
              {intl.formatMessage(msgs.cta)}
            </LinkButton>
            <S.FreeLink
              type="button"
              onClick={() => handleDismiss('stay_on_free')}
              data-testid="pronunciation-upsell-skip"
            >
              {intl.formatMessage(msgs.skip)}
            </S.FreeLink>
          </S.Actions>
        </S.Details>
      </S.Layout>
    </S.Modal>
  );
};
